import { useState } from 'react';
import { CheckIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  useApproveBackdatedHoursRequest,
  useDeclineBackdatedHoursRequest,
} from '../../hooks/useBackdatedHoursRequests';
import { Modal } from '../common/Modal';
import { formatDisplayDate } from '../../utils/format';
import { toErrorMessage } from '../../utils/toErrorMessage';
import type { PendingBackdatedHoursRequest } from '../../services/api/backdatedHoursRequests';

/**
 * Phase 113 (MKS-208, D-10) — the approve/decline table shared by the two
 * surfaces the phase ships: the panel on the project detail page (for the PM)
 * and the central pending queue (for admins). Exactly how join requests do it.
 */
interface BackdatedHoursRequestsTableProps {
  requests: PendingBackdatedHoursRequest[];
  /** The central queue spans projects and needs the column; the panel does not. */
  showProject?: boolean;
}

export function BackdatedHoursRequestsTable({
  requests,
  showProject = false,
}: BackdatedHoursRequestsTableProps) {
  const approveMutation = useApproveBackdatedHoursRequest();
  const declineMutation = useDeclineBackdatedHoursRequest();

  const [approving, setApproving] = useState<PendingBackdatedHoursRequest | null>(null);
  const [declining, setDeclining] = useState<PendingBackdatedHoursRequest | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  // D-09 — approving into an already-invoiced month is allowed, but only after
  // the approver has acknowledged where the hours will actually land. A
  // blocking acknowledgement, not a tooltip.
  const [invoicedAcknowledged, setInvoicedAcknowledged] = useState(false);
  // MKS-266 — the values the ENTRY will be written with. Seeded from the
  // request; the request row itself keeps the employee's originals either way.
  const [approveHours, setApproveHours] = useState('');
  const [approveTask, setApproveTask] = useState('');
  const [approveDescription, setApproveDescription] = useState('');

  const openApprove = (req: PendingBackdatedHoursRequest) => {
    setApproving(req);
    setInvoicedAcknowledged(false);
    setApproveHours(String(req.hours));
    setApproveTask(req.task ?? '');
    setApproveDescription(req.description ?? '');
  };

  const parsedApproveHours = Number(approveHours);
  const approveHoursValid =
    approveHours.trim() !== '' &&
    Number.isFinite(parsedApproveHours) &&
    parsedApproveHours > 0 &&
    parsedApproveHours <= 24;

  const handleApprove = async () => {
    if (!approving) return;
    if (!approveHoursValid) return;
    try {
      await approveMutation.mutateAsync({
        id: approving.id,
        input: {
          hours: parsedApproveHours,
          task: approveTask,
          description: approveDescription,
        },
      });
      setApproving(null);
    } catch {
      // Surfaced in the modal.
    }
  };

  const handleDecline = async () => {
    if (!declining) return;
    try {
      await declineMutation.mutateAsync({
        id: declining.id,
        input: { reason: declineReason.trim() || undefined },
      });
      setDeclining(null);
    } catch {
      // Surfaced in the modal.
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
            </th>
            {showProject && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Hours
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Task
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Reason
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {requests.map((req) => (
            <tr key={req.id} className="hover:bg-gray-50">
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {req.userName || req.userEmail}
                </div>
                <div className="text-xs text-gray-500">{req.userEmail}</div>
              </td>
              {showProject && (
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                  {req.projectName ?? `#${req.projectId}`}
                </td>
              )}
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                <div>{formatDisplayDate(req.date)}</div>
                {req.monthInvoiced && (
                  <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-100 text-amber-800">
                    <ExclamationTriangleIcon className="w-3 h-3" />
                    {req.month} already invoiced
                  </span>
                )}
                {!req.monthLocked && (
                  <span className="block mt-1 text-[11px] text-gray-400">
                    {req.month} is no longer locked
                  </span>
                )}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                {req.hours}
              </td>
              <td className="px-4 py-4 text-sm text-gray-500">{req.task || '—'}</td>
              <td className="px-4 py-4 text-sm text-gray-500 max-w-xs">
                <span className="block truncate" title={req.reason}>
                  {req.reason}
                </span>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-right">
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => openApprove(req)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckIcon className="w-3.5 h-3.5" />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDeclining(req);
                      setDeclineReason('');
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                    Decline
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Approve */}
      <Modal
        isOpen={!!approving}
        onClose={() => setApproving(null)}
        title={`Approve ${approving?.hours ?? ''}h on ${formatDisplayDate(approving?.date ?? null)}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Approving writes the entry on{' '}
            <span className="font-medium">{approving?.userName || approving?.userEmail}</span>&rsquo;s
            behalf, already approved — it does not go through the timesheet approval flow again. You
            can correct the hours, the task and the description before you approve; the request
            itself keeps what was asked for.
          </p>

          {/* MKS-266 — the three values the entry is written with, editable.
              The request's own hours/task/description are left alone, so the
              gap between what was asked for and what was approved stays
              visible in the audit trail. */}
          <div className="space-y-3">
            <div className="text-sm bg-gray-50 rounded-lg p-3 space-y-1">
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Project</span>
                <span className="text-gray-900">{approving?.projectName ?? '—'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Reason given</span>
                <span className="text-gray-900 text-right">{approving?.reason}</span>
              </div>
            </div>

            <div>
              <label
                htmlFor="backdated-approve-hours"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Hours
              </label>
              <input
                id="backdated-approve-hours"
                type="number"
                step="0.1"
                min="0.1"
                max="24"
                value={approveHours}
                onChange={(e) => setApproveHours(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {!approveHoursValid && (
                <p className="mt-1 text-xs text-red-600">Enter hours between 0 and 24.</p>
              )}
              {approving && approveHoursValid && parsedApproveHours !== approving.hours && (
                <p className="mt-1 text-xs text-gray-500">Requested: {approving.hours} h</p>
              )}
            </div>

            <div>
              <label
                htmlFor="backdated-approve-task"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Task
              </label>
              <input
                id="backdated-approve-task"
                type="text"
                maxLength={255}
                value={approveTask}
                onChange={(e) => setApproveTask(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {approving && approveTask.trim() !== (approving.task ?? '') && (
                <p className="mt-1 text-xs text-gray-500">Requested: {approving.task || '—'}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="backdated-approve-description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Description
              </label>
              <textarea
                id="backdated-approve-description"
                rows={2}
                maxLength={1000}
                value={approveDescription}
                onChange={(e) => setApproveDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {approving && approveDescription.trim() !== (approving.description ?? '') && (
                <p className="mt-1 text-xs text-gray-500">
                  Requested: {approving.description || '—'}
                </p>
              )}
            </div>
          </div>

          {/* D-09 — blocking, because the money lands somewhere other than
              where the approver probably expects. */}
          {approving?.monthInvoiced && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2">
              <p className="text-sm text-amber-900 flex gap-2">
                <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 text-amber-600" />
                <span>
                  <span className="font-medium">{approving.month} has already been invoiced.</span>{' '}
                  These hours will NOT appear on the invoice that was already issued — they show up
                  in the To Be Invoiced queue under the NEXT month, marked “carried over from{' '}
                  {approving.month}”, and go out on that invoice.
                </span>
              </p>
              <label className="flex items-center gap-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  checked={invoicedAcknowledged}
                  onChange={(e) => setInvoicedAcknowledged(e.target.checked)}
                  className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                />
                I understand where these hours will be billed.
              </label>
            </div>
          )}

          {approveMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {toErrorMessage(approveMutation.error, 'Failed to approve the request')}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setApproving(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApprove}
              disabled={
                approveMutation.isPending ||
                (!!approving?.monthInvoiced && !invoicedAcknowledged) ||
                !approveHoursValid
              }
              title={
                approving?.monthInvoiced && !invoicedAcknowledged
                  ? 'Acknowledge where the hours will be billed first.'
                  : undefined
              }
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {approveMutation.isPending ? 'Approving…' : 'Approve & create entry'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Decline */}
      <Modal
        isOpen={!!declining}
        onClose={() => setDeclining(null)}
        title={`Decline ${declining?.hours ?? ''}h on ${formatDisplayDate(declining?.date ?? null)}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            No entry is created. The requester sees the reason you give here.
          </p>

          <div>
            <label
              htmlFor="backdated-decline-reason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Reason (optional)
            </label>
            <textarea
              id="backdated-decline-reason"
              value={declineReason}
              maxLength={1000}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="e.g. These hours belong on the maintenance project"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {declineMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {toErrorMessage(declineMutation.error, 'Failed to decline the request')}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setDeclining(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDecline}
              disabled={declineMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {declineMutation.isPending ? 'Declining…' : 'Decline request'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default BackdatedHoursRequestsTable;
